'use client';

import React from 'react';
import { useCurrentMobileHeader } from './MobileHeaderProvider';
import Header from '@/components/header/common/Header';
import { useMediaQuery } from '@/hooks/useMediaQuery';

function useHasMounted() {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  return mounted;
}

const BREAKPOINTS = { sm: 576, md: 768, lg: 992, xl: 1200, xxl: 1400 } as const;
type BpKey = keyof typeof BREAKPOINTS;

type Props = {
  mode?: 'bootstrap' | 'mediaQuery';
  DesktopHeader?: React.ComponentType;
  breakpoint?: number | BpKey;
};

/** 同步 Bootstrap 類名，依傳入 breakpoint 產生 d-*-block/none */
function buildResponsiveClasses(bp: number | BpKey) {
  const key: BpKey =
    typeof bp === 'number'
      ? ((Object.entries(BREAKPOINTS).find(([, v]) => v === bp)?.[0] as BpKey) ?? 'md')
      : bp;

  return {
    desktopWrap: `d-none d-${key}-block`,
    mobileWrap: `d-block d-${key}-none`,
  };
}

/** 讀取是否全關旗標（由 useDisableAllHeader 設定） */
function useDisableAllHeaderFlag() {
  const [off, setOff] = React.useState(false);

  React.useEffect(() => {
    const read = () =>
      typeof document !== 'undefined' && document.body?.dataset?.disableAllHeader === 'true';

    setOff(read());

    const obs =
      typeof MutationObserver !== 'undefined' ? new MutationObserver(() => setOff(read())) : null;

    if (obs && typeof document !== 'undefined') {
      obs.observe(document.body, {
        attributes: true,
        attributeFilter: ['data-disable-all-header'],
      });
    }
    return () => obs?.disconnect();
  }, []);

  return off;
}

const ResponsiveHeader: React.FC<Props> = ({
  mode = 'bootstrap',
  DesktopHeader = Header,
  breakpoint = 'md',
}) => {
  // ⚠️ 所有 hooks 都在最上面、每次 render 都會呼叫，避免 hooks 數量不一致
  const MobileHeader = useCurrentMobileHeader();

  const minWidth = typeof breakpoint === 'number' ? breakpoint : BREAKPOINTS[breakpoint];
  const hasMounted = useHasMounted();
  // 即使在 bootstrap 模式暫時用不到，也一律呼叫，保持 hooks 序/數一致
  const isDesktop = useMediaQuery(`(min-width: ${minWidth}px)`);
  const disableAll = useDisableAllHeaderFlag();

  if (disableAll) {
    // 這裡已經在上面呼叫完所有 hooks 了，現在提早返回不會破壞 hooks 規則
    return null;
  }

  if (mode === 'bootstrap') {
    const classes = buildResponsiveClasses(breakpoint);
    return (
      <>
        <div className={classes.desktopWrap}>
          <DesktopHeader />
        </div>
        <div className={classes.mobileWrap}>
          {MobileHeader ? <MobileHeader /> : <DesktopHeader />}
        </div>
      </>
    );
  }

  if (!hasMounted) return null;
  return isDesktop ? <DesktopHeader /> : MobileHeader ? <MobileHeader /> : <DesktopHeader />;
};

export default ResponsiveHeader;
